import React, { useState, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import api from "../../../../api/api";
import NavItem from "react-bootstrap/NavItem";
import NavLink from "react-bootstrap/NavLink";
import Badge from "react-bootstrap/Badge";
import Collapse from "react-bootstrap/Collapse";
import DropdownItem from "react-bootstrap/DropdownItem";
import Toast from "react-bootstrap/Toast";
import ToastContainer from "react-bootstrap/ToastContainer";
import { useNavigate } from "react-router-dom";
import { formatNotificationDate } from "../../../../utils/notificationDate";
import { useTranslation } from "react-i18next";
import "./Notifications.css";

const isCriticalNotification = (notification) => {
  if (!notification) return false;
  if (String(notification.field_status || "").toLowerCase() === "critical") return true;
  return /critical|critique/i.test(
    `${notification.object || ""} ${notification.description || ""}`,
  );
};

const getNotificationTone = (notification) => {
  const status = String(notification?.field_status || "").toLowerCase();
  const content = `${notification?.object || ""} ${notification?.description || ""}`;

  if (status === "stale" || /non actualis|obsol|stale/i.test(content)) return "stale";
  if (status === "critical" || /critical|critique/i.test(content)) return "critical";
  if (status === "optimal" || /optimal/i.test(content)) return "optimal";
  if (status === "full" || /\bfull\b|pleine capacit|satur/i.test(content)) return "full";
  if (notification?.type === "Warning") return "stale";
  if (notification?.type === "Success") return "optimal";
  return "info";
};

const NotificationToneIcon = ({ tone }) => {
  const icons = {
    critical: "warning",
    optimal: "check_circle",
    full: "water_drop",
    stale: "schedule",
    info: "notifications",
  };
  return <i className="material-icons">{icons[tone] || icons.info}</i>;
};

const Notifications = ({ userId: propUserId }) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [notifList, setNotifList] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [currentToast, setCurrentToast] = useState(null);
  const navigate = useNavigate();
  const socketRef = useRef(null);
  const toastTimeoutRef = useRef(null);
  const notificationIdsRef = useRef(new Set());
  const initialToastShownRef = useRef(false);
  const initReceivedRef = useRef(false);
  const isInitialLoadRef = useRef(true); // 🆕 Pour tracker le premier chargement

  // 🔹 Récupérer l'userId depuis localStorage
  const getUserId = () => {
    if (propUserId) return propUserId;
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user?.id;
  };

  // 🔹 Récupérer l'user UUID (utilisé pour socket)
  const getUserUuid = () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user?.uid || user?.id || propUserId;
  };

  // 🔹 Ouvrir / fermer panneau
  const toggleNotifications = async () => {
    const newVisible = !visible;
    setVisible(newVisible);

    // ✅ Si on FERME le panneau → on marque tout comme vu (comme Facebook)
    if (!newVisible) {
      try {
        await api.put("/api/notifications/view-all");
        setNotifs((prev) => prev.map((n) => ({ ...n, is_view: "1" })));
        setNotifList(0);
        console.log("✅ Toutes les notifications marquées comme vues");
      } catch (err) {
        console.error("Erreur lors du marquage des notifications:", err);
      }
    }
  };

  // 🔹 Marquer une notif spécifique comme vue
  const markAsRead = async (notifId) => {
    try {
      await api.put(`/api/notifications/${notifId}/view`);
      setNotifs((prev) =>
        prev.map((n) => (n.id === notifId ? { ...n, is_view: "1" } : n))
      );
      setNotifList((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Erreur lors du marquage de la notification:", err);
    }
  };

  const IconByType = (type) => {
    switch (type) {
      case "Warning":
        return <i className="material-icons">&#xe002;</i>;
      case "Info":
        return <i className="material-icons">&#xe645;</i>;
      case "Success":
        return <i className="material-icons">&#xE031;</i>;
      default:
        return <i className="material-icons">&#xE7F4;</i>;
    }
  };

  const goToAllNotifs = () => navigate("/notifications");

  // "/notifications" n'autorise pas ROLE_SUPPLIER (protectedRoutes.js) : ce
  // lien était mort pour ce rôle (section 11, risque documenté).
  const currentUserRole = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}")?.role;
    } catch {
      return null;
    }
  })();
  const canViewAllNotifications = currentUserRole !== "ROLE_SUPPLIER";

  // 🔔 Afficher un toast
  const displayToast = (notif) => {
    setCurrentToast(notif);
    setShowToast(true);

    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);

    toastTimeoutRef.current = setTimeout(() => {
      setShowToast(false);
    }, 8000);
  };

  // ⚡ Initialisation - 🆕 Sans dépendances inutiles
  useEffect(() => {
    const userId = getUserId();
    if (!userId) {
      console.error("❌ Aucun userId trouvé dans localStorage");
      return;
    }

    // 🔹 Charger les notifs initiales
    const fetchInitialNotifs = async () => {
      try {
        const response = await api.get("/notifications");
        const notifications = response.data.data || [];
        const clean = notifications.filter((n) => !n.deleted_at);
        notificationIdsRef.current = new Set(clean.map((notification) => notification.id));
        setNotifs(clean);
        const unreadCount = clean.filter((n) => n.is_view === "0" || n.is_view === 0).length;
        setNotifList(unreadCount);
        console.log("📬 Notifications chargées:", clean.length, "dont non lues:", unreadCount);

        isInitialLoadRef.current = false;

        // À la connexion, afficher une seule fois la notification la plus
        // récente, même si elle a été reçue pendant l'absence de l'utilisateur.
        if (!initialToastShownRef.current && clean.length > 0) {
          const latestNotification = [...clean].sort((a, b) => {
            const aDate = new Date(a.observed_at || a.created_at).getTime();
            const bDate = new Date(b.observed_at || b.created_at).getTime();
            return bDate - aDate;
          })[0];
          initialToastShownRef.current = true;
          displayToast(latestNotification);
        }
      } catch (err) {
        console.error("Erreur lors du chargement des notifications:", err);
      }
    };

    if (!socketRef.current) {
      const socketToken = () => {
        const raw=localStorage.getItem('token');
        try {const value=JSON.parse(raw);return typeof value==='string'?value:value?.token;}
        catch (_) {return raw;}
      };
      let authRetryAttempted=false;
      socketRef.current = io(`${process.env.REACT_APP_BASE_URL_SOCKET}`, {
        auth: (done) => {
          done({token:socketToken()});
        },
        transports: ["websocket"],
        reconnection: true,
      });

      socketRef.current.on("connect", () => {
        authRetryAttempted=false;
        console.log("✅ Socket connecté:", socketRef.current.id);
        const userUuid = getUserUuid();
        console.log("➡️ Emission register_user avec:", userUuid);
        socketRef.current.emit("register_user", userUuid);
      });

      socketRef.current.on("user_registered", () => {
  fetchInitialNotifs();
});
      socketRef.current.on('notification_auth_required',async()=>{
        if(authRetryAttempted){socketRef.current?.disconnect();return;}
        authRetryAttempted=true;
        await fetchInitialNotifs();
        socketRef.current?.emit('register_user',{uid:getUserUuid(),token:socketToken()});
      });

      socketRef.current.on("connect_error", (error) =>
        console.error("❌ Erreur de connexion socket:", error)
      );
    }

    const socket = socketRef.current;

    // 🔹 init_notifications (socket)
// Dans Notifications.jsx

const handleInitNotifications = (list) => {
  console.log("📥 init_notifications reçu:", list?.length || 0);
  if (Array.isArray(list)) {
    initReceivedRef.current = true;
    const clean = list.filter((n) => !n.deleted_at);
    clean.forEach((notification) => notificationIdsRef.current.add(notification.id));
    
    // ✅ Fusionner avec les notifs existantes SANS doublons
    setNotifs((prev) => {
      const existing = new Set(prev.map(n => n.id));
      const newNotifs = clean.filter(n => !existing.has(n.id));
      return [...newNotifs, ...prev];
    });
    
    setNotifList(clean.length);
    isInitialLoadRef.current = false;
  }
};

const handleNewNotification = (notif) => {
  console.log("📩 Nouvelle notification reçue:", notif);

  if (notif && notif.deleted_at) {
    return;
  }

  if (!notif || notificationIdsRef.current.has(notif.id)) {
    console.log("⚠️ Notification déjà présente, ignorée");
    return;
  }
  notificationIdsRef.current.add(notif.id);

  setNotifs((prev) => [notif, ...prev]);

  setNotifList((prev) => prev + 1);

  if (!isInitialLoadRef.current) {
    initialToastShownRef.current = true;
    displayToast(notif);
  }
};
const handleUpdatedNotification = (notif) => {
  if (!notif || notif.deleted_at) return;
  notificationIdsRef.current.add(notif.id);
  setNotifs((previous) => {
    const exists = previous.some((item) => item.id === notif.id);
    return exists
      ? previous.map((item) => (item.id === notif.id ? notif : item))
      : [notif, ...previous];
  });
};
// ✅ Trier : non lues en premier
const hasUnread = notifs.some((n) => n.is_view === "0" || n.is_view === 0);

    // 🔹 Notification vue ailleurs
    const handleViewedNotification = (notif) => {
      console.log("👁️ Notification vue:", notif);
      setNotifs((prev) =>
        prev.map((n) => (n.id === notif.id ? { ...n, is_view: "1" } : n))
      );
      setNotifList((prev) => Math.max(0, prev - 1));
    };

    // 🔹 Toutes vues
    const handleAllViewed = () => {
      console.log("✅ Toutes les notifications vues");
      setNotifs((prev) => prev.map((n) => ({ ...n, is_view: "1" })));
      setNotifList(0);
    };

    // 🔹 Notification supprimée
    const handleDeletedNotification = ({ id }) => {
      console.log('🗑️ notification deleted received', id);
      notificationIdsRef.current.delete(id);
      setNotifs((prev) => prev.filter((n) => n.id !== id));
      setNotifList((prev) => Math.max(0, prev - 1));
    };

    // Écouteurs socket
    socket.on("init_notifications", handleInitNotifications);
    socket.on("new_notification", handleNewNotification);
    socket.on("notification:updated", handleUpdatedNotification);
    socket.on("notification:viewed", handleViewedNotification);
    socket.on("notification:deleted", handleDeletedNotification);
    socket.on("notification:all_viewed", handleAllViewed);

    return () => {
      socket.off("init_notifications", handleInitNotifications);
      socket.off("new_notification", handleNewNotification);
      socket.off("notification:updated", handleUpdatedNotification);
      socket.off("notification:viewed", handleViewedNotification);
      socket.off("notification:all_viewed", handleAllViewed);
      socket.off("notification:deleted", handleDeletedNotification);
    };
  }, [propUserId]); // 🆕 Seulement propUserId comme dépendance

  // 🔹 Nettoyage
  useEffect(() => {
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  // ✅ Trier : non lues en premier (calculé à chaque render depuis notifs)
 // ✅ Trier : non lues en premier (calculé à chaque render depuis notifs)
  const sortedNotifs = [...notifs].sort((a, b) => {
    const aUnread = a.is_view === "0" || a.is_view === 0;
    const bUnread = b.is_view === "0" || b.is_view === 0;
    if (aUnread !== bUnread) return aUnread ? -1 : 1;
    const aDate = new Date(a.observed_at || a.created_at).getTime();
    const bDate = new Date(b.observed_at || b.created_at).getTime();
    return bDate - aDate;
  });

  // ✅ N'afficher que les 5 premières dans le dropdown
  const unreadCount = notifs.filter((n) => n.is_view === "0" || n.is_view === 0).length;
  const needsScroll = sortedNotifs.length > 5;
  const visibleNotifs = needsScroll ? sortedNotifs : sortedNotifs.slice(0, 5);
  const latestCriticalNotificationId = [...notifs]
    .filter(isCriticalNotification)
    .sort((a, b) => {
      const aDate = new Date(a.observed_at || a.created_at).getTime();
      const bDate = new Date(b.observed_at || b.created_at).getTime();
      return bDate - aDate;
    })[0]?.id;
  const currentToastTone = getNotificationTone(currentToast);

  return (
    <>
      <NavItem className="border-right dropdown notifications">
        <NavLink className="nav-link-icon text-center" onClick={toggleNotifications}>
          <div className="nav-link-icon__wrapper">
            <i className="material-icons">&#xE7F4;</i>
            {notifs.some((n) => n.is_view === "0") && (
              <Badge pill bg="danger">{notifList}</Badge>
            )}
          </div>
        </NavLink>

         <Collapse in={visible}>
  <div
    className="dropdown-menu dropdown-menu-small"
    style={
      needsScroll
        ? { maxHeight: "400px", overflowY: "auto" }
        : undefined
    }
  >
    {notifs.length === 0 ? (
      <div className="text-muted m-4">No notifications</div>
    ) : (
      visibleNotifs.map((notif, idx) => {
        const isUnread = notif.is_view === "0" || notif.is_view === 0;
        const isHighlightedCritical =
          isCriticalNotification(notif) && notif.id === latestCriticalNotificationId;
        const isFirstRead = !isUnread && visibleNotifs[idx - 1] &&
          (visibleNotifs[idx - 1].is_view === "0" || visibleNotifs[idx - 1].is_view === 0);

        return (
          <React.Fragment key={notif.id || idx}>
            {idx === 0 && isUnread && (
              <div className="notification__section-title px-3 pt-2 pb-1 text-uppercase small fw-bold text-primary">
                Nouveau
              </div>
            )}
            {isFirstRead && (
              <div className="notification__section-title px-3 pt-2 pb-1 text-uppercase small fw-bold text-muted">
                Plus tôt
              </div>
            )}
            <DropdownItem
              className={`notification-message${isHighlightedCritical ? " notification-message--critical" : ""}`}
              onClick={() => markAsRead(notif.id)}
              style={{
                backgroundColor: isHighlightedCritical
                  ? undefined
                  : isUnread
                    ? "var(--sf-night-info-fill, #0d6efd)"
                    : "var(--sf-night-surface, #ffffff)",
                color: isHighlightedCritical || isUnread ? "#ffffff" : "inherit",
                borderLeft: isHighlightedCritical
                  ? undefined
                  : isUnread
                    ? "3px solid var(--sf-night-info, #0d6efd)"
                    : "3px solid transparent",
              }}
            >
                  <div className="notification__icon-wrapper">
                    <div className="notification__icon">{IconByType(notif.type)}</div>
                  </div>
                  <div className="notification__content">
                    <span
                      className="notification__category"
                      style={{
                        color: isHighlightedCritical || isUnread ? "#ffffff" : "var(--sf-night-text, #000000)",
                        fontWeight: 700,
                        display: "block",
                      }}
                    >
                      {t(notif.object, notif.object)}
                    </span>
                    <p>{t(notif.description, notif.description)}</p>
                    <small
                      style={{
                        color: isHighlightedCritical || isUnread
                          ? "rgba(255, 255, 255, 0.88)"
                          : "var(--sf-night-text-secondary, #6c757d)",
                        display: "block",
                        marginTop: "4px",
                      }}
                    >
                      {formatNotificationDate(notif.observed_at || notif.created_at)}
                    </small>
                  </div>
                
                </DropdownItem>
          </React.Fragment>
        );
      })
    )}
            {notifs.length > 0 && canViewAllNotifications && (
              <DropdownItem className="notification__all text-center" onClick={goToAllNotifs}>
                View all Notifications
              </DropdownItem>
            )}
          </div>
        </Collapse>
      </NavItem>

      <ToastContainer position="top-end" className="notification-toast-container">
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          className={`notification-state-toast notification-toast--${currentToastTone}`}
          role="alert"
          aria-live={currentToastTone === "critical" ? "assertive" : "polite"}
        >
          <Toast.Header closeButton>
            <span className="notification-toast__icon" aria-hidden="true">
              <NotificationToneIcon tone={currentToastTone} />
            </span>
            <span className="notification-toast__heading me-auto">
              <small>{t("new_notification", "Nouvelle notification")}</small>
              <strong>{t(currentToast?.object, currentToast?.object)}</strong>
            </span>
          </Toast.Header>
          <Toast.Body>
            <p>{t(currentToast?.description, currentToast?.description)}</p>
            <time dateTime={currentToast?.observed_at || currentToast?.created_at || undefined}>
              {formatNotificationDate(currentToast?.observed_at || currentToast?.created_at)}
            </time>
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export default Notifications;
